import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Checkbox, Select } from 'syngenta-digital-cropwise-react-ui-kit';
import ChevronDown from 'assets/chevron_down.svg';
import RenderTag, { RenderTagProps } from './RenderTag';
import MaxTagPlaceholder from './MaxTagPlaceholder';

import './index.css';

/** Option key and display label (`value` is the visible label). */
export interface FilterBySelectOption {
  key: string;
  value: string;
}

const defaultSelectDataTestId = 'filter-by-select-input';
const defaultRenderTagDataTestId = 'filter-by-select-render-tag';
const defaultCloseAlt = 'close button';
const defaultSuffixIconAlt = 'Select dropdown icon';

export type FilterBySelectInputProps = {
  options: FilterBySelectOption[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  selectDataTestId?: string;
  renderTagDataTestId?: string;
  closeButtonAlt?: string;
  suffixIconAlt?: string;
  formatOmittedCount?: (count: number) => string;
  showSearch?: boolean;
  maxTagCount?: number | 'responsive';
};

export default function FilterBySelectInput({
  options,
  value,
  onChange,
  placeholder,
  className = 'filter-by-select-container',
  style,
  selectDataTestId = defaultSelectDataTestId,
  renderTagDataTestId = defaultRenderTagDataTestId,
  closeButtonAlt = defaultCloseAlt,
  suffixIconAlt = defaultSuffixIconAlt,
  formatOmittedCount,
  showSearch = false,
  maxTagCount = 'responsive'
}: Readonly<FilterBySelectInputProps>) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close the dropdown when clicking outside the component
  useEffect(() => {
    if (!open) {
      return () => {};
    }

    const handleMouseDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener('mousedown', handleMouseDown, true);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
    };
  }, [open]);

  const tagRender = useCallback(
    (props: Pick<RenderTagProps, 'value' | 'onClose'>) => (
      <RenderTag
        value={props.value}
        onClose={props.onClose}
        options={options}
        closeButtonAlt={closeButtonAlt}
        dataTestId={renderTagDataTestId}
      />
    ),
    [options, closeButtonAlt, renderTagDataTestId]
  );

  const renderMaxTagPlaceholder = useCallback(
    (omittedValues: unknown[]) => (
      <MaxTagPlaceholder
        omittedValues={omittedValues}
        formatOmittedCount={formatOmittedCount}
      />
    ),
    [formatOmittedCount]
  );

  return (
    <div ref={containerRef} className={className} style={style}>
      <Select
        data-testid={selectDataTestId}
        value={value}
        onChange={onChange}
        open={open}
        onDropdownVisibleChange={setOpen}
        getPopupContainer={() => containerRef.current ?? document.body}
        showArrow
        suffixIcon={
          <img
            src={ChevronDown}
            style={{ transform: open ? 'rotate(180deg)' : undefined }}
            height={18}
            width={18}
            alt={suffixIconAlt}
          />
        }
        mode="multiple"
        style={{ width: '100%' }}
        placeholder={placeholder}
        tagRender={tagRender}
        optionLabelProp="label"
        maxTagCount={maxTagCount}
        maxTagPlaceholder={renderMaxTagPlaceholder}
        showSearch={showSearch}
      >
        {options.map(option => (
          <Select.Option
            className="filter-by-select-option"
            key={option.key}
            value={option.key}
            label={option.value}
          >
            <div className="flex items-center h-8 pl-4 py-1.5">
              <Checkbox checked={value.includes(option.key)} className="mr-4" />
              <div className="ml-2">{option.value}</div>
            </div>
          </Select.Option>
        ))}
      </Select>
    </div>
  );
}
